import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import {
  LogOut, Download, RefreshCw, Mail, Users, Building2, HandHeart, Inbox, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminMe, adminStats, adminContact, adminNewsletter, adminPartnership, adminVolunteer,
  adminLogout,
} from "@/lib/api";

const TABS = [
  { key: "contact", label: "Contact", icon: Mail, columns: ["created_at", "name", "email", "subject", "organisation", "message"] },
  { key: "newsletter", label: "Newsletter", icon: Inbox, columns: ["created_at", "email", "name"] },
  { key: "partnership", label: "Partnerships", icon: Building2, columns: ["created_at", "organisation", "contact_name", "email", "role", "partnership_type", "message"] },
  { key: "volunteer", label: "Volunteers", icon: HandHeart, columns: ["created_at", "name", "email", "phone", "location", "interests", "availability", "message"] },
];

const LOADERS = {
  contact: adminContact,
  newsletter: adminNewsletter,
  partnership: adminPartnership,
  volunteer: adminVolunteer,
};

// ----- Helpers (extracted to reduce component complexity) -----
function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function escapeCsv(value) {
  const s = String(value ?? "").replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

function toCSV(rows, columns) {
  const header = columns.join(",");
  const body = rows.map((r) => columns.map((c) => escapeCsv(r[c])).join(",")).join("\n");
  return `${header}\n${body}`;
}

function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function rowKey(row, idx) {
  return row.id || `${row.email || ""}-${row.created_at || idx}`;
}

// ----- Small sub-components -----
function StatCard({ tab, value, active, onClick }) {
  const Icon = tab.icon;
  const surface = active ? "bg-ink text-bone" : "bg-white border border-stone";
  const iconColor = active ? "text-crimson" : "text-ink/55";
  const arrowColor = active ? "text-bone/55" : "text-ink/30";
  const numberColor = active ? "text-bone" : "text-ink";
  const labelColor = active ? "text-bone/55" : "text-ink/55";
  return (
    <button
      onClick={onClick}
      data-testid={`stat-${tab.key}`}
      className={`text-left rounded-2xl p-5 transition-all hover:-translate-y-0.5 ${surface}`}
    >
      <div className="flex items-center justify-between">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <ChevronRight className={`w-4 h-4 ${arrowColor}`} />
      </div>
      <div className={`mt-4 text-3xl font-heading font-bold ${numberColor}`}>{value ?? "—"}</div>
      <div className={`mt-1 text-xs uppercase tracking-wider ${labelColor}`}>{tab.label}</div>
    </button>
  );
}

function CellContent({ column, row, onStopPropagation }) {
  const value = row[column];
  if (column === "created_at") {
    return <span className="whitespace-nowrap text-xs text-ink/55">{formatDate(value)}</span>;
  }
  if (column === "email") {
    return (
      <a
        href={`mailto:${value}`}
        onClick={onStopPropagation}
        className="text-ink hover:text-crimson break-all"
      >
        {value}
      </a>
    );
  }
  if (column === "message") {
    return <span className="line-clamp-2 max-w-md text-ink/70">{value || "—"}</span>;
  }
  return <span className="break-words">{value || "—"}</span>;
}

function EmptyState({ label }) {
  return (
    <div className="p-12 text-center">
      <Users className="w-10 h-10 text-ink/20 mx-auto mb-4" />
      <p className="text-ink/55">No {label.toLowerCase()} submissions yet.</p>
    </div>
  );
}

function RowDetails({ row, columns }) {
  return (
    <tr className="bg-sand/50 border-t border-stone">
      <td colSpan={columns.length} className="px-5 py-5">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
          {columns.map((c) => {
            const display = c === "created_at" ? formatDate(row[c]) : (row[c] || "—");
            return (
              <div key={c} className="flex gap-3">
                <dt className="text-ink/55 capitalize w-32 flex-shrink-0">{c.replace(/_/g, " ")}</dt>
                <dd className="text-ink break-words">{display}</dd>
              </div>
            );
          })}
        </dl>
      </td>
    </tr>
  );
}

// ----- Main component -----
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState("contact");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [openRow, setOpenRow] = useState(null);

  // Bootstrap: verify session cookie + fetch initial stats. Runs once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, s] = await Promise.all([adminMe(), adminStats()]);
        if (cancelled) return;
        setMe(u);
        setStats(s);
        setAuthed(true);
      } catch {
        if (cancelled) return;
        setAuthed(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadActiveTab = useCallback(async (tabKey) => {
    setLoadingRows(true);
    setOpenRow(null);
    try {
      const data = await LOADERS[tabKey]();
      setRows(data);
    } catch {
      toast.error("Couldn't load submissions");
    } finally {
      setLoadingRows(false);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadActiveTab(active);
  }, [active, authed, loadActiveTab]);

  const refresh = useCallback(async () => {
    setLoadingRows(true);
    try {
      const [s, r] = await Promise.all([adminStats(), LOADERS[active]()]);
      setStats(s);
      setRows(r);
      toast.success("Refreshed");
    } catch {
      toast.error("Refresh failed");
    } finally {
      setLoadingRows(false);
    }
  }, [active]);

  const logout = useCallback(async () => {
    try { await adminLogout(); } catch { /* ignore — clear client state anyway */ }
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const exportCSV = useCallback(() => {
    const cols = TABS.find((t) => t.key === active).columns;
    downloadCSV(`${active}-${new Date().toISOString().split("T")[0]}.csv`, toCSV(rows, cols));
    toast.success("CSV downloaded");
  }, [active, rows]);

  const toggleRow = useCallback((idx) => {
    setOpenRow((prev) => (prev === idx ? null : idx));
  }, []);

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  if (!authChecked) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-ink/55">Loading…</div>;
  }
  if (!authed) return <Navigate to="/admin/login" replace />;

  const activeTab = TABS.find((t) => t.key === active);
  const Cols = activeTab.columns;
  const HeaderIcon = activeTab.icon;

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-ink text-bone sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            <img src="/logo.jpg" alt="Aotūroa Academy" className="h-8 w-auto rounded bg-white p-0.5" />
            <span className="font-heading font-semibold text-sm">Admin Console</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-bone/55 truncate max-w-[200px]">{me?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full transition-all"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {TABS.map((t) => (
            <StatCard
              key={t.key}
              tab={t}
              value={stats?.[t.key]}
              active={active === t.key}
              onClick={() => setActive(t.key)}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-ink flex items-center gap-3">
            <HeaderIcon className="w-6 h-6 text-crimson" /> {activeTab.label} submissions
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              disabled={loadingRows}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-1.5 text-sm bg-white border border-stone hover:border-ink/40 px-4 py-2.5 rounded-full transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRows ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={exportCSV}
              disabled={rows.length === 0}
              data-testid="admin-csv-btn"
              className="inline-flex items-center gap-1.5 text-sm bg-ink text-white hover:bg-ink/85 px-4 py-2.5 rounded-full transition-all disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        <div className="bg-white border border-stone rounded-2xl overflow-hidden" data-testid={`admin-table-${active}`}>
          {loadingRows && <div className="p-12 text-center text-ink/55">Loading submissions…</div>}
          {!loadingRows && rows.length === 0 && <EmptyState label={activeTab.label} />}
          {!loadingRows && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sand text-ink/60 uppercase text-[10px] tracking-[0.15em]">
                  <tr>
                    {Cols.map((c) => (
                      <th key={c} className="px-5 py-4 text-left font-semibold whitespace-nowrap">
                        {c.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const key = rowKey(r, i);
                    return (
                      <React.Fragment key={key}>
                        <tr
                          onClick={() => toggleRow(i)}
                          className="border-t border-stone hover:bg-sand/40 cursor-pointer"
                          data-testid={`admin-row-${active}-${i}`}
                        >
                          {Cols.map((c) => (
                            <td key={c} className="px-5 py-4 text-ink/80 align-top">
                              <CellContent column={c} row={r} onStopPropagation={stopPropagation} />
                            </td>
                          ))}
                        </tr>
                        {openRow === i && <RowDetails row={r} columns={Cols} />}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-ink/45 text-center">
          {rows.length} {rows.length === 1 ? "record" : "records"} · sorted newest first
        </p>
      </main>
    </div>
  );
}
